import React, { useMemo } from 'react';
import { GuestSiteComponentProps } from '@apps/websiteCustomJsonComponents/types';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { SunIcon, CloudIcon, RainIcon } from './WeatherIcons';
import { PrecipMetric, HumidityMetric, WindSpeedMetric, UvIndexMetric, TemperatureRangeBar } from './WeatherMetrics';
import { WeatherReportContentV103, WeatherReportItineraryLeg } from '../WeatherReport.types';
import { useTranslation } from '@shared/core/i18n';
import { useWeatherReportColors } from './WeatherReport.colors';
import { normalizeWeatherReportContent, deriveLegTitle, formatWeatherDateRange, formatWeatherDayParts, computeDailyBounds, isIsoDateToday } from '../WeatherReport.helpers';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';

//

const pickIcon = (summary?: string, size: number = 24) => {
  const s = (summary || '').toLowerCase();
  if (s.includes('rain') || s.includes('shower')) return <RainIcon size={size} />;
  if (s.includes('cloud')) return <CloudIcon size={size} />;
  return <SunIcon size={size} />;
};

//

export const WeatherReportGuest: React.FC<GuestSiteComponentProps> = ({ jsonComponentData }) => {
  const derived = useWeatherReportColors();
  const { t } = useTranslation('websiteEditor');
  const weatherTrans = t('customPage', 'weatherReport');
  const { language } = useLocaleContext();
  const rawContent = jsonComponentData.containerContent.content;
  const data: WeatherReportContentV103 = useMemo(() => normalizeWeatherReportContent(rawContent), [rawContent]);
  const legs: WeatherReportItineraryLeg[] = data.itinerary || [];

  const renderLeg = (leg: WeatherReportItineraryLeg, index: number) => {
    const tz = leg.location?.timezone;
    const fallbackTitle = weatherTrans.cities?.fallback ? weatherTrans.cities.fallback({ index: index + 1 }) : `City ${index + 1}`;
    const title = deriveLegTitle(leg, fallbackTitle);
    const range = leg.dateRange ? formatWeatherDateRange(leg.dateRange, { locale: language, timeZone: tz }) : '';
    const today = leg.daily?.[0];
    const bounds = computeDailyBounds(leg.daily);
    const temperatureGradientBuilder = derived.gradients.temperature;
    const temperatureGradient = typeof temperatureGradientBuilder === 'function' ? temperatureGradientBuilder(bounds) : temperatureGradientBuilder;
    const dayCount = leg.daily?.length ?? 0;

    return (
      <Box
        key={`${leg.label || leg.location?.name || index}`}
        borderRadius={4}
        boxShadow="0px 2px 8px rgba(0,0,0,0.06), 0px 12px 24px rgba(0,0,0,0.04)"
        overflow="hidden"
        marginBottom={5}
        backgroundColor={derived.tokens.cardBg}
      >
        <Box
          padding={5}
          backgroundColor={derived.tokens.cardBg}
        >
          <Flex
            alignItems={['flex-start', null, 'center']}
            justifyContent="space-between"
            gap={6}
            flexDirection={['column', null, 'row']}
          >
            <Box maxWidth={['unset', null, '60ch']}>
              <TextV2 typographyVariant="hed2">{title}</TextV2>
              {range && (
                <TextV2
                  typographyVariant="label2"
                  color={derived.tokens.textSecondary}
                >
                  {range}
                </TextV2>
              )}
              {leg.guestHighlights && (
                <TextV2
                  marginTop={3}
                  typographyVariant="body1"
                  color={derived.tokens.textPrimary}
                >
                  {leg.guestHighlights}
                </TextV2>
              )}
            </Box>

            {today && (
              <Flex
                alignItems="center"
                gap={4}
              >
                <Box color={derived.tokens.icon}>{pickIcon(today.summary, 24)}</Box>
                <Flex
                  alignItems="baseline"
                  gap={2}
                >
                  <TextV2 typographyVariant="hed3">{Math.round(today.highTempC)}°C</TextV2>
                  <TextV2
                    typographyVariant="hed4"
                    color={derived.tokens.textSecondary}
                  >
                    / {Math.round(today.lowTempC)}°C
                  </TextV2>
                </Flex>
              </Flex>
            )}
          </Flex>

          {today && (
            <TextV2
              marginTop={3}
              color={derived.tokens.textSecondary}
              typographyVariant="body2"
            >
              {today.summary}
            </TextV2>
          )}
        </Box>

        {Array.isArray(leg.daily) && leg.daily.length > 0 && (
          <Box
            padding={5}
            backgroundColor={derived.tokens.cardBg}
            borderTopWidth="1px"
            borderTopStyle="solid"
            borderTopColor={derived.tokens.border}
          >
            <TextV2
              typographyVariant="hed6"
              color={derived.tokens.textSecondary}
              marginBottom={2}
            >
              {weatherTrans.guest.nDayForecast({ count: dayCount })}
            </TextV2>
            <Flex
              gap={5}
              alignItems="flex-start"
              flexDirection={['column', null, 'row']}
            >
              {today && (
                <Box
                  order={[1, null, 2]}
                  width={['100%', null, '320px']}
                  flexShrink={0}
                >
                  <Flex
                    flexDirection="column"
                    gap={3}
                  >
                    <PrecipMetric
                      precip01={today.precipChance}
                      gradients={derived.gradients}
                      pillBg={derived.tokens.pillBg}
                      pillBorder={derived.tokens.pillBorder}
                      textColor={derived.tokens.textSecondary}
                      iconColor={derived.tokens.icon}
                    />
                    <HumidityMetric
                      pct={today.humidityPct}
                      gradients={derived.gradients}
                      pillBg={derived.tokens.pillBg}
                      pillBorder={derived.tokens.pillBorder}
                      textColor={derived.tokens.textSecondary}
                      iconColor={derived.tokens.icon}
                    />
                    <WindSpeedMetric
                      kph={today.windSpeedKph}
                      gradients={derived.gradients}
                      pillBg={derived.tokens.pillBg}
                      pillBorder={derived.tokens.pillBorder}
                      textColor={derived.tokens.textSecondary}
                      iconColor={derived.tokens.icon}
                    />
                    <UvIndexMetric
                      uv={today.uvIndex}
                      gradients={derived.gradients}
                      pillBg={derived.tokens.pillBg}
                      pillBorder={derived.tokens.pillBorder}
                      textColor={derived.tokens.textSecondary}
                      iconColor={derived.tokens.icon}
                    />
                  </Flex>
                </Box>
              )}

              <Box
                order={[2, null, 1]}
                flex={1}
              >
                <Box>
                  {leg.daily.map((d, idx) => {
                    const dayParts = formatWeatherDayParts(d.date, { locale: language, timeZone: tz });
                    const isLast = idx === leg.daily.length - 1;
                    const isTodayLabel = idx === 0 && isIsoDateToday(d.date, { timeZone: tz });
                    const dayLabel = isTodayLabel ? weatherTrans.guest.today() : dayParts.dow;
                    return (
                      <Flex
                        key={`${d.date}-${idx}`}
                        alignItems="center"
                        gap={3}
                        paddingY={2}
                        borderBottomWidth={isLast ? undefined : '1px'}
                        borderBottomStyle={isLast ? undefined : 'solid'}
                        borderBottomColor={isLast ? undefined : derived.tokens.border}
                      >
                        <Flex
                          alignItems="center"
                          gap={2}
                          minWidth={[90, null, 120]}
                        >
                          <Box width="48px">
                            <TextV2
                              typographyVariant="label2"
                              color={derived.tokens.textSecondary}
                              textTransform="capitalize"
                            >
                              {dayLabel}
                            </TextV2>
                          </Box>
                          <Box color={derived.tokens.icon}>{pickIcon(d.summary, 18)}</Box>
                          {typeof d.precipChance === 'number' && (
                            <TextV2
                              typographyVariant="label3"
                              color={derived.tokens.textSecondary}
                            >
                              {Math.round(d.precipChance * 100)}%
                            </TextV2>
                          )}
                        </Flex>

                        <Box
                          width="44px"
                          textAlign="right"
                        >
                          <TextV2
                            typographyVariant="label2"
                            color={derived.tokens.textSecondary}
                          >
                            {Math.round(d.lowTempC)}°
                          </TextV2>
                        </Box>

                        <Box
                          flex={1}
                          minWidth={80}
                        >
                          <TemperatureRangeBar
                            min={bounds.min}
                            max={bounds.max}
                            low={d.lowTempC}
                            high={d.highTempC}
                            height={8}
                            gradient={temperatureGradient}
                          />
                        </Box>

                        <Box width="48px">
                          <TextV2 typographyVariant="label2">{Math.round(d.highTempC)}°</TextV2>
                        </Box>
                      </Flex>
                    );
                  })}
                </Box>
              </Box>
            </Flex>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {data.guestHighlights && (
        <Box marginBottom={4}>
          <TextV2
            typographyVariant="body1"
            color={derived.tokens.textSecondary}
          >
            {data.guestHighlights}
          </TextV2>
        </Box>
      )}
      {legs.map((leg, i) => renderLeg(leg, i))}
    </Box>
  );
};
